/**
 * Consultations: consultants list, my bookings, book – data layer hooks.
 */
import { useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchConsultants, fetchMyConsultations, bookConsultation, type BookConsultationPayload } from './api';
import type { ConsultantApi, MyConsultationBooking } from './apiTypes';
import { isConsultationsFeatureEnabled } from '../config';
import { consultationQueryKeys } from './keys';

export function useConsultants(customer_id: number, selected_date: string) {
  const consultations_enabled = isConsultationsFeatureEnabled();
  const query = useQuery({
    queryKey: consultationQueryKeys.consultants(customer_id, selected_date),
    queryFn: () => fetchConsultants(customer_id, selected_date),
    /** Guest browsing uses `customer_id` 0; API still returns the public consultant list for a date. */
    enabled: consultations_enabled && selected_date.length >= 10,
  });

  const data = useMemo((): ConsultantApi[] => (Array.isArray(query.data) ? query.data : []), [query.data]);
  const error = useMemo(() => (query.error instanceof Error ? query.error : null), [query.error]);
  const refetch = useCallback(() => query.refetch(), [query]);

  return useMemo(
    () => ({
      data,
      isLoading: query.isLoading,
      error,
      refetch,
    }),
    [data, query.isLoading, error, refetch],
  );
}

export function useMyConsultations(customer_id: number) {
  const consultations_enabled = isConsultationsFeatureEnabled();
  const query = useQuery({
    queryKey: consultationQueryKeys.myConsultations(customer_id),
    queryFn: () => fetchMyConsultations(customer_id),
    enabled: consultations_enabled && customer_id > 0,
  });

  const data = useMemo((): MyConsultationBooking[] => (Array.isArray(query.data) ? query.data : []), [query.data]);
  const error = useMemo(() => (query.error instanceof Error ? query.error : null), [query.error]);
  const refetch = useCallback(() => query.refetch(), [query]);

  return useMemo(
    () => ({
      data,
      isLoading: query.isLoading,
      error,
      refetch,
    }),
    [data, query.isLoading, error, refetch],
  );
}

export function useBookConsultation() {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (payload: BookConsultationPayload) => bookConsultation(payload),
    onSuccess: (_data, variables) => {
      client.invalidateQueries({ queryKey: consultationQueryKeys.myConsultations(variables.customer_id) });
      client.invalidateQueries({ queryKey: ['consultations'] });
    },
  });
  return mutation;
}
